import { Suspense, lazy } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, UserCog, Users, Shield, Sliders } from 'lucide-react';
import { useAuth } from '@onvice/portal-shell-api';
import ClonModuleButton from '../../components/ClonModuleButton';

const AdressbuchPersonenListe = lazy(() => import('./AdressbuchMitarbeiterListe'));
const AdminAdressbuchPage     = lazy(() => import('../admin/AdminAdressbuchPage'));

type SubTab = 'personen' | 'gruppen' | 'module' | 'abschnitte';

interface SubTabDef {
  slug: SubTab;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  adminOnly?: boolean;
}

// Joerg 2026-06-14: „Adressbuch = Personen, Gruppen, Module" — Person und
// Mitarbeiter sind synonym. Kunden gehoeren NICHT ins Adressbuch (eigener
// Pfad, heute /m/stammdaten/kunden bzw. /uebersicht/auswertungen).
// Abschnitte = acl.field_access-Matrix-Pflege (Sektionen/Tabs der Mitarbeiterakte).
const SUB_TABS: SubTabDef[] = [
  { slug: 'personen',   label: 'Personen',   icon: UserCog },
  { slug: 'gruppen',    label: 'Gruppen',    icon: Users,   adminOnly: true },
  { slug: 'module',     label: 'Module',     icon: Shield,  adminOnly: true },
  { slug: 'abschnitte', label: 'Abschnitte', icon: Sliders, adminOnly: true },
];

function Fallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
}

interface Props {
  /** Embed-Modus: Klon-Knopf weglassen. Joerg 2026-05-29. */
  embedded?: boolean;
}

export default function UebersichtAdressbuch({ embedded = false }: Props = {}) {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const visibleTabs = SUB_TABS.filter((t) => !t.adminOnly || isAdmin);
  const requested = (searchParams.get('tab') as SubTab | null);
  const activeTab: SubTab = visibleTabs.some((t) => t.slug === requested)
    ? (requested as SubTab)
    : 'personen';

  return (
    <div>
      <div className="border-b border-default bg-card/30">
        <div className="max-w-7xl mx-auto px-4 h-11 flex items-center gap-1 overflow-x-auto">
          {visibleTabs.map((t) => {
            const active = t.slug === activeTab;
            const Icon = t.icon;
            const search = t.slug === 'personen' ? '' : `?tab=${t.slug}`;
            return (
              <Link
                key={t.slug}
                to={{ search }}
                className={
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ' +
                  (active
                    ? 'bg-primary/15 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-card')
                }
                aria-current={active ? 'page' : undefined}
              >
                <Icon size={14} />
                <span>{t.label}</span>
              </Link>
            );
          })}
          {!embedded && (
            <div className="ml-auto">
              <ClonModuleButton widgetSlug="adressbuch.embed" defaultName="Mein Adressbuch" icon="BookUser" />
            </div>
          )}
        </div>
      </div>

      <Suspense fallback={<Fallback />}>
        {activeTab === 'personen'   ? <AdressbuchPersonenListe /> : null}
        {activeTab === 'gruppen'    ? <AdminAdressbuchPage initialTab="groups"   hideTabBar /> : null}
        {activeTab === 'module'     ? <AdminAdressbuchPage initialTab="modules"  hideTabBar /> : null}
        {activeTab === 'abschnitte' ? <AdminAdressbuchPage initialTab="sections" hideTabBar /> : null}
      </Suspense>
    </div>
  );
}
